// src/pages/CustomerList.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
  FaPowerOff,
} from "react-icons/fa";
import { TbArrowsSort } from "react-icons/tb";
import { FiRotateCcw } from "react-icons/fi";
import toast from "react-hot-toast";
import Select from "react-select";

import ConfirmDialog from "../components/ConfirmDialog";
import {
  useGetAllCustomersQuery,
  useDeleteCustomerMutation,
  useBulkDeleteCustomersMutation,
  useActivateCustomerMutation,
  useDeactivateCustomerMutation,
} from "../toolkitServices/customerApi";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { handleDropDowns } from "../config/dropDownHelpers";
import { useSelector } from "react-redux";

/* ------------ Tiny spinner (same vibe as URD / LabelledStock) ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const headerCell =
  "px-3 sm:px-4 py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-3 sm:px-4 py-1.5 text-xs sm:text-sm";

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused
      ? "0 0 0 1px rgba(148,129,206,.25)"
      : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const chipCls = (isActive) =>
  `px-2 py-0.5 rounded-full text-[11px] ${
    isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"
  }`;

/* Skeleton rows */
const SkeletonRows = ({ cols = 11, rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={dataCell}>
            <div className="h-3 w-20 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};
const CustomerList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  // header controls
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // filters: applied vs draft
  const [appliedFilters, setAppliedFilters] = useState({
    status: "", // "", "active", "inactive"
    branchId: "", // 👈 NEW
  });
  const [draftFilters, setDraftFilters] = useState(appliedFilters);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const statusFilter = appliedFilters.status;
  const branchFilter = appliedFilters.branchId;

  // Branches for dropdown
  const { data: branchesRes, isLoading: branchesLoading } =
    useGetAllBranchesQuery();
  const branches = branchesRes?.data || branchesRes?.items || [];
  const branchOptions = handleDropDowns({
    key: "branch",
    branches,
  });

  const statusSelectRef = useRef(null);

  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setIsFilterOpen(true);
  };

  useEffect(() => {
    if (!isFilterOpen) return;
    const t = setTimeout(() => {
      if (statusSelectRef.current) {
        statusSelectRef.current.focus?.();
      }
    }, 80);
    return () => clearTimeout(t);
  }, [isFilterOpen]);

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setIsFilterOpen(false);
  };

  const resetDraftFilters = () =>
    setDraftFilters({
      status: "",
      branchId: "",
    });

  // sorting
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

  // Build API params
  const activeParam =
    statusFilter === "" ? undefined : statusFilter === "active" ? true : false;

  const { data, isFetching, isSuccess, isLoading } = useGetAllCustomersQuery({
    page,
    limit,
    search,
    active: activeParam,
    sortBy,
    sortOrder,
    branchId: branchFilter || undefined, // 👈 NEW
  });

  const rows = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data],
  );

  // pagination meta
  const totalDocs =
    data?.total ?? data?.pagination?.totalDocs ?? rows.length ?? 0;
  const totalPages = data?.pages ?? data?.pagination?.totalPages ?? 1;
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  // keyboard shortcut: Ctrl+P -> create
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/members/customer-list/create-customer");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // debounce page reset on filters/search/limit/sort
  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [search, appliedFilters, limit, sortBy, sortOrder]);

  // selection (bulk delete)
  const [selectedIds, setSelectedIds] = useState(new Set());
  useEffect(
    () => setSelectedIds(new Set()),
    [page, limit, appliedFilters, search, isSuccess],
  );

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const allIdsOnPage = rows.map((r) => String(r._id));
  const allSelectedOnPage =
    allIdsOnPage.length > 0 && allIdsOnPage.every((id) => selectedIds.has(id));

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        allIdsOnPage.forEach((id) => next.delete(id));
      } else {
        allIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });

  // mutations
  const [deleteCustomer, { isLoading: deletingOne }] =
    useDeleteCustomerMutation();
  const [bulkDelete, { isLoading: deletingMany }] =
    useBulkDeleteCustomersMutation();
  const [activateCustomer] = useActivateCustomerMutation();
  const [deactivateCustomer] = useDeactivateCustomerMutation();

  // confirm dialog
  const [confirm, setConfirm] = useState({
    open: false,
    mode: null, // 'single' | 'bulk'
    payload: null, // { id, label } or { ids, count }
  });
  const openConfirmSingle = (item) =>
    setConfirm({
      open: true,
      mode: "single",
      payload: { id: item._id, label: item.customerName || item.customerId },
    });
  const openConfirmBulk = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setConfirm({
      open: true,
      mode: "bulk",
      payload: { ids, count: ids.length },
    });
  };
  const closeConfirm = () =>
    setConfirm({ open: false, mode: null, payload: null });

  const handleConfirm = async () => {
    try {
      if (confirm.mode === "single") {
        const { id, label } = confirm.payload || {};
        await deleteCustomer(id).unwrap();
        toast.success(`Deleted ${label || "customer"}`);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(String(id));
          return next;
        });
      } else if (confirm.mode === "bulk") {
        const { ids } = confirm.payload || {};
        const res = await bulkDelete(ids).unwrap();
        toast.success(`Deleted ${res?.deleted || 0}`);
        setSelectedIds(new Set());
      }
    } catch (e) {
      const msg = e?.data?.msg || e?.error || "Operation failed";
      toast.error(msg);
    } finally {
      closeConfirm();
    }
  };

  // sorting click
  const onSort = (field) => {
    if (sortBy === field) setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };
  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  // quick status toggle
  const toggleActive = async (row) => {
    try {
      if (row.isActive !== false) {
        await deactivateCustomer(row._id).unwrap();
        toast.success("Deactivated");
      } else {
        await activateCustomer(row._id).unwrap();
        toast.success("Activated");
      }
    } catch (e) {
      const msg = e?.data?.msg || e?.error || "Update failed";
      toast.error(msg);
    }
  };

  // chips (active + search + branch)
  const chips = useMemo(() => {
    const list = [];
    if (statusFilter)
      list.push({ key: "status", label: `Status: ${statusFilter}` });

    if (branchFilter) {
      const b = branchOptions.find(
        (x) => String(x.value) === String(branchFilter),
      );
      list.push({
        key: "branch",
        label: `Branch: ${b?.label || "Unknown"}`,
      });
    }

    if (search) list.push({ key: "search", label: `Search: ${search}` });
    return list;
  }, [statusFilter, search, branchFilter, branchOptions]);

  const clearChip = (key) => {
    if (key === "status") setAppliedFilters((f) => ({ ...f, status: "" }));
    else if (key === "search") setSearch("");
    else if (key === "branch")
      setAppliedFilters((f) => ({ ...f, branchId: "" }));
    setPage(1);
  };

  const clearAllChips = () => {
    setSearch("");
    setAppliedFilters({ status: "", branchId: "" });
    setPage(1);
  };

  const selectedCount = selectedIds.size;

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Global loader overlay (first load) */}
      {isFetching && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading customers…</span>
          </div>
        </div>
      )}

      {/* Top header bar */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              Customer List
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Manage customer profiles and account status
            </p>
          </div>
          {isFetching && rows.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 lg:inline-flex">
            {totalDocs || 0} customer{totalDocs === 1 ? "" : "s"}
          </span>
          <Link
            to="/members/customer-list/create-customer"
            className="inline-flex h-9 items-center gap-1 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full sm:px-4"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* Filters bar – search + Filters button */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search */}
          <div className="flex flex-col gap-[1px] flex-1 min-w-[180px]">
            <label className="text-[11px] font-medium text-slate-600">
              Search
            </label>
            <input
              type="text"
              placeholder="Name, phone, company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Filters button */}
          <div className="flex flex-col gap-[1px] w-auto">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              onClick={openFilters}
              className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 outline-[#6b59c6]"
              title="Advanced filters"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm"
            >
              {c.label}
              <button
                className="text-slate-500 hover:text-red-500"
                onClick={() => clearChip(c.key)}
                title="Clear"
              >
                <FaTimesCircle />
              </button>
            </span>
          ))}
          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* ===== TABLE VIEW ===== */}
      <div
        className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]"
        aria-busy={isFetching}
      >
        <table className="w-full min-w-[1100px] text-left text-xs sm:text-sm">
          <thead className="sticky top-0 z-40 bg-[#ece8fb] text-[10px] uppercase text-[#4b3d91] sm:text-[11px]">
            <tr>
              {/* Select all */}
              <th className="px-3 sm:px-4 py-1.5 w-10">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={allSelectedOnPage}
                  onChange={toggleSelectAllOnPage}
                  title="Select all on page"
                />
              </th>

              <th className={headerCell}>Customer ID</th>
              <th className={headerCell}>Branch</th>
              {/* 👈 NEW */}
              <th className={headerCell}>Name</th>
              <th className={headerCell}>Company</th>
              <th className={headerCell}>Contact</th>
              <th className={headerCell}>City</th>
              <th className={headerCell}>State</th>
              <th className={headerCell}>Active</th>
              <th className={headerCell}>Created On {sortIcon("createdAt")}</th>
              <th className={`${headerCell} sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px]`}><span className="sm:hidden">Act</span><span className="hidden sm:inline">Action</span></th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence initial={false}>
              {isFetching && rows.length ? (
                <SkeletonRows cols={11} rows={8} />
              ) : rows.length > 0 ? (
                rows.map((item, index) => {
                  const checked = selectedIds.has(String(item._id));
                  const isActive = item.isActive !== false; // default true if missing

                  const city = item.permanentAddress?.city || item.city || "-";
                  const state =
                    item.permanentAddress?.state || item.state || "-";

                  return (
                    <motion.tr
                      key={item._id || `${item.customerId}-${index}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className="group border-t transition hover:bg-[#f6f3ff]"
                    >
                      {/* checkbox */}
                      <td className="px-3 sm:px-4 py-1.5">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={checked}
                          onChange={() => toggleSelect(item._id)}
                          title="Select"
                        />
                      </td>

                      <td className={`${dataCell} font-medium`}>
                        <div className="inline-block px-3 py-1 rounded-full border bg-white text-center shadow-sm">
                          {item.customerId || "-"}
                        </div>
                      </td>

                      {/* Branch name */}
                      <td className={dataCell}>{item.branchName || "-"}</td>

                      <td className={dataCell}>{item.customerName || "-"}</td>
                      <td className={dataCell}>{item.companyName || "-"}</td>
                      <td className={dataCell}>{item.contact || "-"}</td>
                      <td className={dataCell}>{city}</td>
                      <td className={dataCell}>{state}</td>
                      <td className={dataCell}>
                        <span className={chipCls(isActive)}>
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className={dataCell}>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )
                          : "-"}
                      </td>
                      <td
                        className={`${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]`}
                      >
                        {/* Edit */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Edit"
                          onClick={() =>
                            navigate(`/members/customer-list/${item._id}/edit`)
                          }
                        >
                          <FaPencilAlt />
                        </motion.button>

                        {/* Activate / Deactivate */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-md border text-[10px] sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px] ${
                            isActive
                              ? "border-amber-100 text-amber-600 hover:bg-amber-50"
                              : "border-emerald-100 text-emerald-600 hover:bg-emerald-50"
                          }`}
                          title={isActive ? "Deactivate" : "Activate"}
                          onClick={() => toggleActive(item)}
                        >
                          <FaPowerOff />
                        </motion.button>

                        {/* Delete */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:opacity-40 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Delete"
                          disabled={deletingOne}
                          onClick={() => {
                            if (canDelete(user)) {
                              openConfirmSingle(item);
                            } else {
                              toast.error(
                                "You don't have permission to delete",
                              );
                            }
                          }}
                        >
                          <FaTrashAlt />
                        </motion.button>
                      </td>
                    </motion.tr>
                  );
                })
              ) : (
                !isLoading &&
                !isFetching && (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td
                      colSpan={11}
                      className="text-center text-gray-500 py-6 text-xs"
                    >
                      No matching customer records found. Try changing filters
                      or search text.
                    </td>
                  </motion.tr>
                )
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Footer / Pagination */}
      <div className="shrink-0 flex items-center justify-between px-2 py-1.5 sm:px-4 sm:py-2 border-t text-[10px] sm:text-sm bg-slate-50/60 gap-2">
        <div className="hidden text-slate-600 sm:block">
          Showing{" "}
          <span className="font-semibold">
            {rows.length ? (page - 1) * limit + 1 : 0}
          </span>{" "}
          to{" "}
          <span className="font-semibold">
            {rows.length ? (page - 1) * limit + rows.length : 0}
          </span>{" "}
          of <span className="font-semibold">{totalDocs}</span> customers
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={!hasPrev || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">&lsaquo;</span>
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{
                  value: totalPages ? page : 1,
                  label: String(totalPages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, totalPages) },
                  (_, index) => ({ value: index + 1, label: String(index + 1) }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, totalPages)}</span>
            </span>
            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select value={{ value: totalPages ? page : 1, label: String(totalPages ? page : 1) }} options={Array.from({ length: Math.max(1, totalPages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))} onChange={(option) => setPage(Number(option?.value) || 1)} isDisabled={isFetching} isSearchable={false} menuPlacement="top" menuPosition="fixed" menuPortalTarget={document.body} menuShouldScrollIntoView={false} styles={paginationSelectStyles} className="w-[58px]" aria-label="Select page number" />
              <span>of {Math.max(1, totalPages)}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={!hasNext || isFetching}
              onClick={() =>
                setPage((p) =>
                  totalPages ? Math.min(totalPages, p + 1) : p + 1,
                )
              }
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">&rsaquo;</span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedCount > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-t flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-[11px] shadow-sm">
              {selectedCount}
            </div>
            <span className="text-[11px] text-slate-700">
              {selectedCount} customer{selectedCount > 1 ? "s" : ""} selected
            </span>
          </div>
          <button
            disabled={deletingMany}
            onClick={openConfirmBulk}
            className="ml-auto inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-full text-[11px] disabled:opacity-40"
          >
            {deletingMany ? "Deleting..." : "Delete selected"}
          </button>
        </div>
      )}

      {/* Filter modal – Status + Branch */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">
                    Advanced filters
                  </h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Filter customers by status and branch.
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setIsFilterOpen(false)}
                  aria-label="Close filters"
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-4 text-[11px]">
                {/* Status */}
                <div>
                  <label className="block text-slate-600 mb-1">Status</label>
                  <select
                    ref={statusSelectRef}
                    value={draftFilters.status}
                    onChange={(e) =>
                      setDraftFilters((s) => ({
                        ...s,
                        status: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Branch */}
                <div>
                  <label className="block text-slate-600 mb-1">Branch</label>
                  <select
                    value={draftFilters.branchId}
                    onChange={(e) =>
                      setDraftFilters((s) => ({
                        ...s,
                        branchId: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                    disabled={branchesLoading}
                  >
                    <option value="">
                      {branchesLoading ? "Loading branches…" : "All branches"}
                    </option>
                    {branchOptions.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetDraftFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae]"
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirm.open}
        onCancel={closeConfirm}
        onConfirm={handleConfirm}
        title={
          confirm.mode === "bulk"
            ? `Delete ${confirm.payload?.count || 0} customer(s)?`
            : `Delete ${confirm.payload?.label || "customer"}?`
        }
        message={
          confirm.mode === "bulk"
            ? "Are you sure? Deleted customers cannot be retrieved."
            : "Are you sure? Deleted customer cannot be retrieved."
        }
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        loading={deletingOne || deletingMany}
      />
    </div>
  );
};

export default CustomerList;
